import { useEffect, useState } from "react";
import { Popup, PopupBody, PopupFooter } from "../controls";
import { user } from "../../user";
import { useAppDispatch } from "../../effects/useAppDispatch";
import { onBoardingTooltipService } from "../../services";
import { OnBoardingTooltipType } from "../../types/onboarding-tooltips/OnBoardingTooltipType";
import { errorActions } from "../../actions";
import { EmailLink } from "../bidding/common/EmailLink";
import { appConfig } from "../../app-config";
import { usePopupOrder } from "../../effects/usePopupOrder";
import illustration from "../../images/eval-lp/start-eval-bg.jpg";
import { onBoardingTooltipConst, routes } from "../../constants";
import { Link } from "react-router-dom";
import { useEvalSubscription } from "./hooks/userEvalSubscription";

export function EvalPopupAutoOpener() {
    const onBoardingCounters = user.getOnBoardingTooltipCounters();
    const numberOfViews =
        onBoardingCounters.EvalPopup ?? onBoardingTooltipConst.maxCountLimit;
    const { setViewed } = useSetEvalPopupViewed();
    const { canShow, markAsShown, markAsWait } = usePopupOrder("Eval");
    const [visible, setVisible] = useState(false);

    const { isTrial, isTrialEnded, isSubscriptionEnded, subscriptionRemainingDays } = useEvalSubscription();
    const isEvalNewUser = !isTrial && !isTrialEnded && !isSubscriptionEnded && !subscriptionRemainingDays;

    useEffect(() => {
        if (visible || !canShow) return;

        if (isEvalNewUser && numberOfViews < onBoardingTooltipConst.maxCountLimit && appConfig.evalEnabled) {
            setVisible(true);
        } else {
            markAsShown?.();
        }
    }, [visible, numberOfViews, canShow, markAsShown, isEvalNewUser]);

    const handleClose = () => {
        setViewed();
        markAsShown?.();
        setVisible(false);
    };

    if (!visible) return null;

    const openStartEvalPopup = () => {
        handleClose();
        markAsWait("EvalStart");
    };

    return (
        <Popup
            title="Start Your EVAL™ Trial Today"
            renderInBody
            onClose={handleClose}
            modalClass="eval-modal"
        >
            <PopupBody>
                <img className="eval-modal-image" src={illustration} alt="" />
                <h2>
                    Now offering a free trial – experience the power of daily
                    CLO pricing.
                </h2>
                <ul className="list-bullet grey">
                    <li>
                        The trial provides access to the full US BSL universe
                        for <span className="text-medium">three business days</span>.
                    </li>
                    <li>
                        A one-time full data export from the prior month-end is
                        included.
                    </li>
                </ul>
                <p className="margin-b-4">
                    To learn more about KopenTech products and services, contact
                    us at <span className="text-medium"><EmailLink email={appConfig.salesEmailSubscription} /></span> 
                </p>
                <p>
                    <Link to={routes.resourcesEval} target="_blank" className="text-medium">
                        Read more on Resources
                    </Link>
                </p>
            </PopupBody>
            <PopupFooter>
                <button className="btn btn-main" onClick={openStartEvalPopup}>
                    try EVAL™
                </button>
            </PopupFooter>
        </Popup>
    );
}

export const useSetEvalPopupViewed = () => {
    const dispatch = useAppDispatch();

    const setViewed = async () => {
        const onBoardingCounters = user.getOnBoardingTooltipCounters();
        const count = (onBoardingCounters.EvalPopup || 0) + 1;

        try {
            await onBoardingTooltipService.setCount([{ page: OnBoardingTooltipType.evalPopup, count }])
            user.setOnBoardingTooltipCounter(OnBoardingTooltipType.evalPopup, count)
        } catch (e) {
            dispatch(errorActions.error(e));
        }
    }

    return { setViewed }
}
